import React, { forwardRef, Fragment, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import useTextEntities from '../_data_sources/useTextEntities';
import { error } from '_lex/_foundations/phxColors';
import { TextEntity } from 'views/App/Portal/Policy/_types/giq';
import { CreateTextEntityParams } from 'views/App/Portal/Policy/_types/manuals/draft';
import useManageTextEntity from '../_data_sources/useManageTextEntity';

export type TextEntityPickerProps = {
  id: string;
  label: string;
  value?: TextEntity;
  onChange: (value: TextEntity) => void;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  error?: any;
  helperText?: string | undefined;
  usedEntities?: string[]; // List of used text entity IDs
};

interface TextEntityOption extends TextEntity {
  inputValue?: string;
  label?: string;
}

const TextEntityPicker = forwardRef(
  (
    {
      id,
      label,
      value,
      onChange,
      disabled = false,
      required,
      fullWidth,
      usedEntities,
      error,
      helperText,
    }: TextEntityPickerProps,
    ref
  ) => {
    const queryClient = useQueryClient();
    const textEntitiesQuery = useTextEntities();
    const [selectedTextEntityId, setSelectedTextEntityId] = useState<string>(value?.id ?? '');
    const [selectedOption, setSelectedOption] = useState<TextEntityOption | null>();
    const [textEntityOptions, setTextEntityOptions] = useState<TextEntityOption[]>([]);
    const [newTextEntityValue, setNewTextEntityValue] = useState<CreateTextEntityParams>({
      textEntity: '',
      description: '',
    });
    const [newOpen, setNewOpen] = React.useState(false);
    const { createTextEntity } = useManageTextEntity();

    const handleClose = () => {
      setNewTextEntityValue({ textEntity: '', description: '' });
      setNewOpen(false);
    };

    const filter = createFilterOptions<TextEntityOption>();

    const disableSubmit = () => {
      return (
        !newTextEntityValue.textEntity ||
        !newTextEntityValue.description ||
        newTextEntityValue.textEntity.length === 0 ||
        newTextEntityValue.description.length === 0
      );
    };

    const handleSubmit = async () => {
      await createTextEntity(
        newTextEntityValue,
        async (data) => {
          await textEntitiesQuery.refetch();
          if (data?.id) {
            setSelectedTextEntityId(data.id);
          }
        },
        (err) => {
          console.warn('error creating text entity', err);
        }
      );
      handleClose();
    };

    useEffect(() => {
      if (textEntitiesQuery.data?.content && textEntitiesQuery.data.content.length > 0) {
        const opts = textEntitiesQuery.data.content
          .filter((x) => !(usedEntities ?? []).includes(x.id!))
          .sort((a, b) => (a.textEntity ?? '').localeCompare(b.textEntity ?? ''))
          .map((x) => {
            const opt: TextEntityOption = {
              ...x,
            };
            return opt;
          });
        setTextEntityOptions(opts);
      }
    }, [textEntitiesQuery.data]);

    useEffect(() => {
      if (selectedTextEntityId) {
        const selOpt = textEntityOptions.find((x) => x.id === selectedTextEntityId);
        setSelectedOption(selOpt);
        const newVal = selOpt;
        if (newVal) onChange(newVal as TextEntity);
      }
    }, [selectedTextEntityId, textEntityOptions]);

    useEffect(() => {
      setSelectedTextEntityId(value?.id ?? '');
    }, [value]);

    return textEntitiesQuery.isLoading ? (
      <CircularProgress />
    ) : (
      <Fragment>
        <Autocomplete
          value={selectedOption ?? ''}
          fullWidth={fullWidth}
          disabled={disabled}
          onChange={(event, newValue: any) => {
            if (newValue) {
              if (newValue.id) {
                setSelectedTextEntityId(newValue.id);
              } else if (newValue.inputValue) {
                // timeout to avoid instant validation of the dialog's form.
                setTimeout(() => {
                  setNewOpen(true);
                  setNewTextEntityValue({
                    textEntity: newValue.inputValue,
                    description: '',
                  });
                });
              }
            } else {
              setSelectedTextEntityId('');
            }
          }}
          getOptionLabel={(option) => {
            // for example value selected with enter, right from the input
            if (typeof option === 'string') {
              return option;
            }
            if (option.label) {
              return option.label;
            }
            return `${option.description} [${option.textEntity}]`;
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            if (params.inputValue !== '') {
              filtered.push({
                inputValue: params.inputValue,
                label: `Add "${params.inputValue}"`,
              });
            }

            return filtered;
          }}
          id={id}
          options={textEntityOptions}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <li
                key={key}
                {...optionProps}
              >
                <Stack
                  direction='row'
                  spacing={3}
                >
                  {option.description && <Typography>{option.description}</Typography>}
                  {option.textEntity && (
                    <Typography sx={{ color: error[500], fontWeight: 'bold' }}>{`[${option.textEntity}]`}</Typography>
                  )}
                  {option.label && <Typography>{option.label}</Typography>}
                </Stack>
              </li>
            );
          }}
          freeSolo
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              fullWidth
              required={required}
              error={error}
              helperText={helperText}
            />
          )}
        />
        <Dialog
          open={newOpen}
          onClose={handleClose}
        >
          <DialogTitle>New Text Entity</DialogTitle>
          <DialogContent>
            <Stack
              spacing={4}
              sx={{
                minWidth: 400,
              }}
            >
              <DialogContentText>Create a new text entity</DialogContentText>
              <TextField
                autoFocus
                id='textEntity'
                value={newTextEntityValue.textEntity}
                onChange={(event) =>
                  setNewTextEntityValue({
                    ...newTextEntityValue,
                    textEntity: event.target.value,
                  })
                }
                label='Text Entity'
                type='text'
              />
              <TextField
                id='description'
                value={newTextEntityValue.description}
                onChange={(event) =>
                  setNewTextEntityValue({
                    ...newTextEntityValue,
                    description: event.target.value,
                  })
                }
                label='Description'
                type='text'
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant='text'
              color='neutral'
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              disabled={disableSubmit()}
              onClick={() => handleSubmit()}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </Fragment>
    );
  }
);

export default TextEntityPicker;
